import styled from 'styled-components'
import { Divider, P, Wrapper } from '@farewill/ui'
import { FONT, GTR } from '@farewill/ui/tokens'

import SectionStatus from 'components/section-status'

import Dinero from 'dinero.js'
import { QuoteResponseDataProbate } from 'lib/types'
import ProbateOnlyQuoteBreakdown from './probate-only-quote-breakdown'
import ProbateOnlyCopyToClipboard from './probate-only-copy-to-clipboard'
import { formatCurrency, hasDecimals } from 'lib/formatting/currency'
Dinero.globalLocale = 'en-GB'
Dinero.defaultCurrency = 'GBP'

const StyledPrice = styled(P)`
  font-size: ${FONT.SIZE.XL};
  margin-bottom: 0;
  font-weight: ${FONT.WEIGHT.MEDIUM};
`

const StyledList = styled.ul`
  font-size: ${GTR.S};

  li {
    padding: ${GTR.XXS} 0;

    &::before {
      content: '•';
      margin-right: 0.5rem;
    }
  }
`

const StyledRoot = styled.div`
  display: flex;
  flex: 1;
  width: 100%;

  & > * {
    flex: 1;
    width: 100%;
  }
`

const StyledBreakdownSection = styled(Wrapper)`
  margin-top: auto;
`

const ProbateOnlyQuote = ({
  quote,
}: {
  quote: QuoteResponseDataProbate | null
}) => {
  const grantOnlyQuote = quote?.attributes.grantOnly
  const grantOnlyQuoteLoaded = !!grantOnlyQuote

  if (!grantOnlyQuoteLoaded) {
    return (
      <StyledRoot>
        <SectionStatus
          heading="Essential Probate"
          incompleteHeading="Essential Probate"
          complete
        >
          <P size="S">Loading Essential Probate quote...</P>
        </SectionStatus>
      </StyledRoot>
    )
  }

  const {
    amountInPence,
    basePrice,
    courtFeeInPence,
    grantCopyPriceInPence,
    grantCopyQuantity,
    discountAmountInPence,
  } = grantOnlyQuote

  const complexity = grantOnlyQuote.isComplex ? 'complex' : 'simple'

  const discountAmountProbateOnlyQuote = discountAmountInPence || 0

  const amountBeforeDiscount = amountInPence + discountAmountInPence
  const basePriceBeforeVatInPence = Math.round(basePrice / 1.2)
  const amountBeforeVatAndDiscountInPence = Math.round(
    amountBeforeDiscount / 1.2
  )
  const vatInPence = amountBeforeDiscount - amountBeforeVatAndDiscountInPence
  const complexityBeforeVatInPence =
    amountBeforeVatAndDiscountInPence - basePriceBeforeVatInPence
  const disbursementsPriceInPence =
    courtFeeInPence + grantCopyPriceInPence * grantCopyQuantity

  const probateOnlyQuote = (amountInPence || 0) + disbursementsPriceInPence

  const calculatedPrices = {
    totalInPence: probateOnlyQuote,
    basePriceBeforeVatInPence,
    complexityBeforeVatInPence,
    amountBeforeVatAndDiscountInPence,
    vatInPence,
    disbursementsPriceInPence,
  }

  return (
    <StyledRoot>
      <SectionStatus
        heading={`Essential Probate (${complexity})`}
        incompleteHeading="Essential Probate"
        complete
      >
        <P size="S">
          This is a {complexity} estate so our quote for getting probate is:
        </P>
        <StyledPrice>
          {formatCurrency({
            value: probateOnlyQuote,
            valueInPence: true,
            showPence: hasDecimals(probateOnlyQuote / 100),
          })}
        </StyledPrice>
        {discountAmountProbateOnlyQuote > 0 && (
          <P size="S">
            Including a discount of{' '}
            {formatCurrency({
              value: discountAmountProbateOnlyQuote,
              valueInPence: true,
              showPence: hasDecimals(discountAmountProbateOnlyQuote / 100),
            })}
          </P>
        )}
        <P size="S" margin={[0, 0, 'XS']}>
          This fee includes:
        </P>
        <StyledList>
          <li> VAT </li>
          <li>
            {Dinero({ amount: courtFeeInPence || 0 }).toFormat('$0,0')} probate
            registry fee
          </li>
          <li>
            {Dinero({ amount: grantCopyPriceInPence }).toFormat()} per copy of
            the grant
            {grantCopyQuantity > 0 && ` (usually we get ${grantCopyQuantity})`}
          </li>
        </StyledList>
        <StyledBreakdownSection>
          <Divider margin={['M', 0]} />
          <ProbateOnlyQuoteBreakdown
            quote={quote}
            calculatedPrices={calculatedPrices}
          />
          <ProbateOnlyCopyToClipboard
            quote={quote}
            calculatedPrices={calculatedPrices}
          />
        </StyledBreakdownSection>
      </SectionStatus>
    </StyledRoot>
  )
}

export default ProbateOnlyQuote
